import moment, { Moment } from "moment";
import validator from "validator";
import { Error as InputError } from "../copy/input";
import { isHtmlContentEmpty } from "./text";

export const isUrl = (val: string): boolean => {
  if (!val || val.length === 0) return false;
  return validator.isURL(val) || validator.isURL(val.replace("localhost", "foo.bar"));
};

export const isHexColor = (val: string): boolean => /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(val);

export const requiredString = (values, key, errors) => {
  const val = values[key];

  if (!val || !/\S/.test(val)) {
    errors[key] = InputError.Required;
  }
};

export const requiredNumber = (values, key, errors) => {
  const val = values[key];

  if (!val || !/\d+/.test(val)) {
    errors[key] = InputError.Required;
  }
};

export const requiredHtmlContent = (values, key, errors) => {
  const val: string = values[key];

  if (!val || isHtmlContentEmpty(val)) {
    errors[key] = InputError.Required;
  }
};

export const requiredPassword = (values, key, errors) => {
  const val = values[key];

  if (!val || !/\S/.test(val)) {
    errors[key] = InputError.Required;
    return;
  }

  if (val.length < 8) {
    errors[key] = InputError.PasswordTooShort;
    return;
  }

  if (val.length > 64) {
    errors[key] = InputError.PasswordTooLong;
    return;
  }
};

export const requiredEmail = (values, key, errors) => {
  const val = values[key];

  if (!val) {
    errors[key] = InputError.Required;
  } else if (!validator.isEmail(val)) {
    errors[key] = InputError.InvalidEmail;
  }
};

type ValidatorFn = (val: any) => string | undefined;
export const composeValidators =
  (...validators: ValidatorFn[]) =>
  (value: any): string =>
    validators.reduce<string>((error, validator) => error || validator(value), undefined);

export const validateEmail = (val) =>
  validator.isEmail(val ?? "") ? undefined : InputError.InvalidEmail;
export const validateRequired = (val) => (val ? undefined : InputError.Required);
export const validateDate = (val) =>
  !val || moment(val).isValid() ? undefined : InputError.InvalidDate;

export const validateNotIn =
  (collection: string[], message: string = "Value already exists", caseSensitive: boolean = true) =>
  (val: string) => {
    const included = caseSensitive
      ? collection.includes(val)
      : collection.some((v) => v.toLowerCase() === val.toLowerCase());

    return included ? message : undefined;
  };

export const requiredDate = (values, key, errors) => {
  const val = values[key];

  if (!val) {
    errors[key] = InputError.Required;
  } else if (!(val as Moment).isValid()) {
    errors[key] = InputError.InvalidDate;
  }
};

export const optionalEmail = (values, key, errors) => {
  const val = values[key];
  if (val && !validator.isEmail(val)) {
    errors[key] = InputError.InvalidEmail;
  }
};

export const requiredUrl = (values, key, errors) => {
  const val = values[key];
  if (!val) {
    errors[key] = InputError.Required;
  } else if (!isUrl(val)) {
    errors[key] = InputError.InvalidUrl;
  }
};

export const optionalUrl = (values, key, errors) => {
  const val = values[key];
  if (val && !isUrl(val)) {
    errors[key] = InputError.InvalidUrl;
  }
};

export const requiredHexColor = (values, key, errors) => {
  const val = values[key];
  if (val && !isHexColor(val)) {
    errors[key] = InputError.InvalidHexColor;
  }
};

export const requiredGAWebPropertyId = ({
  webProperty,
}: {
  webProperty?: string;
}): Record<string, string> => {
  const errors = {} as any;

  if (webProperty && webProperty.match(/^U|-\d/)) {
    errors.webProperty =
      "Looks like you are using the old Universal Analytics ID. Please check your Google Analytics account and provide the updated GA Property ID which is 9 digits (like 123456789).";
  } else if (webProperty && !/^\d{9}$/.test(webProperty)) {
    errors.webProperty = "Your Google Analytics Property ID must be 9 numbers.";
  }

  return errors;
};

export const requiredGAMeasurementId = (
  {
    gaMeasurementId,
  }: {
    gaMeasurementId?: string;
  },
  webProperty: string,
): Record<string, string> => {
  const errors = {} as any;
  const GA_MEASUREMENT_FORMAT_MESSAGE =
    "Your Google Analytics Measurement ID starts with 'G-' and is followed by a combination of letters and/or numbers.";

  if (!gaMeasurementId) {
    return {};
  }

  // Measurement ID must be G- followed by a combination of letters and numbers, or just letters.
  if (/^G-[0-9]+$/.test(gaMeasurementId) || !/^G-[A-z0-9]+$/.test(gaMeasurementId)) {
    errors.gaMeasurementId = GA_MEASUREMENT_FORMAT_MESSAGE;
  }

  // Measurement ID must not match the Web Property ID, either with or without the G- prefix.
  if (webProperty) {
    const measurementIdNoPrefixMatches = gaMeasurementId.match(/^(?:G-)?([A-z0-9]+)$/);

    if (measurementIdNoPrefixMatches?.length > 0) {
      const measurementIdNoPrefix = measurementIdNoPrefixMatches[1];

      if (measurementIdNoPrefix === webProperty) {
        errors.gaMeasurementId = `Your Google Analytics Measurement ID must be different from your Google Analytics Property ID. ${GA_MEASUREMENT_FORMAT_MESSAGE}`;
      }
    }
  }

  return errors;
};
